import { authHeaders, resolveUrl } from '../api';
import type { QVoiceLang } from './qVoice';

// ─────────────────────────────────────────────────────────────────
// Uppläsning med ElevenLabs (valet "ElevenLabs" under Röst i Q:s inställningar).
// Ljudet hämtas från API:t (/api/q/tts) som håller nyckeln; här spelas det upp
// med ett <audio>. prepare() börjar hämta direkt när en mening kommer, så att
// nästa mening oftast är klar när den förra har lästs upp.
// ─────────────────────────────────────────────────────────────────

export interface TtsVoice { id: string; name: string; description: string }
export interface TtsStatus { available: boolean; voices: TtsVoice[]; error?: string }

export async function fetchTtsStatus(): Promise<TtsStatus> {
  try {
    const res = await fetch(resolveUrl('/api/q/tts'), { headers: authHeaders() });
    if (!res.ok) return { available: false, voices: [] };
    return await res.json() as TtsStatus;
  } catch {
    return { available: false, voices: [] };
  }
}

let statusCache: Promise<TtsStatus> | null = null;
/**
 * Statusen delas av alla Q-paneler och hämtas en gång (ett misslyckat svar hämtas om nästa gång). Förr hämtades
 * den först när panelen öppnades, så svar som kom innan (RQ4: "Q, …" i jakten) lästes med Apples röst.
 */
export function cachedTtsStatus(fetchStatus: () => Promise<TtsStatus> = fetchTtsStatus): Promise<TtsStatus> {
  if (!statusCache) {
    const p = fetchStatus();
    statusCache = p;
    void p.then(st => { if (!st.available && statusCache === p) statusCache = null; });
  }
  return statusCache;
}
export const resetTtsStatusCache = () => { statusCache = null; };

/**
 * Ska ElevenLabs läsa upp? Ja när det är valt och servern inte sagt att det saknas – även innan statusen hunnit
 * hämtas. Misslyckas uppspelningen läser Apples röst (reserven).
 */
export function wantsEleven(engine: string, status: TtsStatus | null | undefined): boolean {
  return engine === 'elevenlabs' && status?.available !== false;
}

export interface PreparedSpeech { url: Promise<string | null>; abort: () => void }

// Tyst wav (0,05 s) byggd i koden: spelas i ett tryck så att Safari tillåter senare uppspelning utan tryck.
function silentWav(): string {
  const samples = 400, rate = 8000, bytes = new Uint8Array(44 + samples * 2);
  const v = new DataView(bytes.buffer);
  const str = (o: number, t: string) => [...t].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, 'RIFF'); v.setUint32(4, 36 + samples * 2, true); str(8, 'WAVE'); str(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, samples * 2, true);
  let bin = ''; bytes.forEach(b => { bin += String.fromCharCode(b); });
  return `data:audio/wav;base64,${btoa(bin)}`;
}

export class ElevenSpeaker {
  private audio: HTMLAudioElement | null = null;
  private listeners = new Set<(speaking: boolean) => void>();
  private stopPlayback: (() => void) | null = null;

  onSpeaking(fn: (speaking: boolean) => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  private emit(speaking: boolean) { this.listeners.forEach(fn => fn(speaking)); }

  private el(): HTMLAudioElement {
    if (!this.audio) { this.audio = new Audio(); this.audio.preload = 'auto'; }
    return this.audio;
  }

  /** Anropas i ett tryck (mikrofon, Prova): låser upp uppspelningen i Safari. */
  unlock() {
    const a = this.el();
    if (a.src && !a.paused) return;
    a.src = silentWav();
    void a.play().catch(() => undefined);
  }

  /** Börjar hämta ljudet för texten. */
  prepare(text: string, lang: QVoiceLang, voiceId?: string): PreparedSpeech {
    const ctrl = new AbortController();
    const url = fetch(resolveUrl('/api/q/tts'), {
      method: 'POST',
      headers: { ...authHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify({ text, lang, voiceId }),
      signal: ctrl.signal,
    })
      .then(async res => (res.ok ? URL.createObjectURL(await res.blob()) : null))
      .catch(() => null);
    return { url, abort: () => ctrl.abort() };
  }

  /** Spelar upp en förberedd text. Löser när den är klar, avbruten eller misslyckad (då false). */
  async play(prepared: PreparedSpeech): Promise<boolean> {
    const url = await prepared.url;
    if (!url) return false;
    const a = this.el();
    return new Promise<boolean>(resolve => {
      const done = (ok: boolean) => {
        a.onended = a.onerror = null;
        this.stopPlayback = null;
        URL.revokeObjectURL(url);
        this.emit(false);
        resolve(ok);
      };
      this.stopPlayback = () => { a.pause(); done(true); };
      a.onended = () => done(true);
      a.onerror = () => done(false);
      a.src = url;
      this.emit(true);
      a.play().catch(() => done(false));
    });
  }

  stop() {
    this.stopPlayback?.();
  }
}

let shared: ElevenSpeaker | null = null;
export const getElevenSpeaker = () => (shared ??= new ElevenSpeaker());
